import PropTypes from 'prop-types';
import { Component, Children, cloneElement } from 'react';
import lodash from 'lodash';

import { DataType } from 'complionAdmin/components/BinderTemplateQuery/DataType/DataType';
import styles from './AutocompleteDataSource.scss';
import searchIcon from 'styles/icons/search-icon.scss';
import { hasSameValueCaseInsensitive } from 'common/util/hasSameValueCaseInsensitive';

const AutocompleteToggleKeyCheck = /^[a-zA-Z0-9.!@?#"$%&:';()*+,/;\-=[\\\]^_{|}<>~` ]$/;

class AutocompleteDataSource extends Component {

    constructor(props, ...args) {
        super(props, ...args);
        const { value } = props;
        this.state = {
            showList: false,
            editorFocused: false,
            editorValue: value || '',
            searchValue: '',
            selectedValue: null,
            invalid: false,
        };
        this.onOutsideClick = this.onOutsideClick.bind(this);
    }

    componentDidMount() {
        document.addEventListener('click', this.onOutsideClick, true);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.onOutsideClick, true);
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            value: lastValue
        } = prevProps;
        const { value } = this.props;
        const {
            showList: lastShowList,
            searchValue: lastSearchValue,
            invalid: lastInvalid,
        } = prevState;
        const {
            showList,
            searchValue,
            invalid,
            editorValue
        } = this.state;
        if (lastValue !== value) {
            this.setState({ editorValue: value });
        }
        const { validate, onValidStateChange } = this.props;
        if (lastShowList !== showList) {
            if (showList) {
                this.selectItemByOffset(0);
            } else {
                this.focusElement(this.editor);
            }
        }
        if (lastSearchValue !== searchValue) {
            this.selectItemByOffset(0);
        }
        if (validate && invalid !== lastInvalid) {
            if (lodash.isFunction(onValidStateChange)) {
                onValidStateChange(this, { hasError: invalid, editorValue });
            }
        }
    }

    onOutsideClick(e) {
        if (this.state.showList) {
            if (!this.container.contains(e.target)) {
                e.stopPropagation();
                this.hideList();
            }
        }
    }

    focusElement(element) {
        if (element && lodash.isFunction(element.focus)) {
            element.focus();
        }
    }

    render() {
        const { children, validate, showDisplayText } = this.props;
        const {
            editorValue,
            showList,
            editorFocused,
            selectedValue,
            invalid
        } = this.state;

        let displayDataType = this.calculateDisplayDataType();

        const errorDecoratorClass = validate && invalid ? styles.autocompleteErrorDecorator : '';

        const childrenElement = children && Children.only(children);

        const hasSelectedValue = ({ value, selectedValues }) => {
            /**
                * Check if the value props has a value,
                * this is set when its a single select autocomplete field,
                * it will be empty if its a multi-selected item.
                * */
            if (!lodash.isEmpty(value)) {
                return true;
            }

            /**
                * Check for multi-value field.
                */
            return !lodash.isEmpty(selectedValues);
        };

        const setPlaceholder = () => {
            const hasSelected = hasSelectedValue({
                value: this.props.value,
                selectedValues: this.props.selectedValues
            });

            if (hasSelected) {
                return null;
            }

            return 'Not Selected';
        };

        const dataTypeEditorChildren = childrenElement
            ? cloneElement(children, {
                ref: dte => this.editor = dte,
                value: editorValue,
                className: `${styles.autocompleteEditor} ${childrenElement.props.className} ${errorDecoratorClass}`,
                dataType: displayDataType,
                placeholder: setPlaceholder(),
                changeHandler: e => this.changeEditorValue(e.target.value),
                onKeyPress: e => this.handleEditorEnterPress(e),
                onFocus: e => this.onEditorFocus(true),
                onBlur: e => this.onEditorBlur(false)
            })
            : null;

        const hintDecorator = showList || !editorFocused ? null : (
            <div className={styles.autocompleteHintDecorator}
                onMouseDown={e => this.handleHintClick(e)}
                data-cy="AutocompleteDataSource">
                <div title="Press Enter" className={searchIcon.searchIcon} />
            </div>
        );

        return (
            <div className={styles.autocompleteContainer}
                ref={e => this.container = e}
                onFocus={e => this.handleFocus(e)}
                onBlur={e => this.handleBlur(e)}
                onKeyPress={e => this.handleAutocompleteKeyPress(e)}
                onKeyDown={e => this.handleAutocompleteKeyDown(e)}
                data-cy="AutocompleteDataSource-autocompleteContainer">
                <div className={styles.autocompleteEditorArea}>
                    {dataTypeEditorChildren}
                    {hintDecorator}
                </div>
                <div className={styles.autocompleteValuesArea} data-cy="AutocompleteDataSource-searchArea">
                    <AutocompleteSearch ref={acs => this.search = acs}
                        showList={showList}
                        showDisplayText={showDisplayText}
                        selectedValue={selectedValue}
                        handleSelectValue={this.handleSelectValue.bind(this)}
                        values={this.getFilteredValues()} />
                </div>
            </div>
        );

    }

    calculateDisplayDataType() {
        // priority:
        // use dataType of selected value
        // otherwise try and find item with the entered value and get dataType
        // otherwise use fallback dataType provided
        // otherwise use smallText
        const { dataType: fallbackDataType } = this.props;
        const { editorValue, selectedValue } = this.state;

        if (selectedValue && selectedValue.dataType) {
            this._lastKnownDataType = selectedValue.dataType;
            return selectedValue.dataType;
        }

        const dataSourceItem = this.findDataSourceItem(editorValue);
        if (dataSourceItem && dataSourceItem.dataType) {
            this._lastKnownDataType = dataSourceItem.dataType;
            return dataSourceItem.dataType;
        }

        if (fallbackDataType) {
            this._lastKnownDataType = fallbackDataType;
            return fallbackDataType;
        }

        if (this._lastKnownDataType) {
            return this._lastKnownDataType;
        }

        return 'small_text';
    }

    findDataSourceItem(value) {
        const { values } = this.props;
        return lodash.find(values, ({ value: comparingValue, dataType }) => hasSameValueCaseInsensitive(
            DataType.getRawValue(dataType, value),
            DataType.getRawValue(dataType, comparingValue)
        ));
    }

    showList(e) {
        const { showList, editorValue } = this.state;
        if (!showList) {
            this.setState({
                searchValue: DataType.getRawValue(this.calculateDisplayDataType(), editorValue),
                showList: true
            });
            e.stopPropagation();
        }
    }

    onEditorFocus(e) {
        this.setState({ editorFocused: true });
    }

    onEditorBlur(e) {
        this.setState({ editorFocused: false });
        this.hideList();
    }

    changeEditorValue(newValue) {
        this.handleChange(newValue);
        this.setState({ editorValue: newValue || '', searchValue: newValue || '' });
    }

    handleChange(newValue) {
        const { onChange } = this.props;
        if (lodash.isFunction(onChange)) {
            onChange(newValue);
        }
        this.validate(newValue);
    }

    getFilteredValues() {
        const { state: { searchValue }, props: { values } } = this;
        const filterPattern = lodash.escapeRegExp(searchValue);
        const filterRegexp = new RegExp(filterPattern, 'i');
        const dataType = this.calculateDisplayDataType();
        const filterFunction = ({ value, text }) => filterRegexp.test(value) || filterRegexp.test(DataType.getRawValue(dataType, value)) || filterRegexp.test(text);
        return lodash.chain(values)
            .filter(filterFunction)
            .sortBy('order').value();
    }

    selectItemByIndex(index) {
        const values = this.getFilteredValues();
        const nextIndex = lodash.clamp(index, 0, values.length - 1);
        this.setState({
            selectedValue: values[nextIndex]
        });
        return values[nextIndex];
    }

    selectItemByOffset(offset) {
        const values = this.getFilteredValues();
        const currentIndex = lodash.findIndex(values, v => lodash.eq(this.state.selectedValue, v));
        const nextIndex = lodash.clamp(currentIndex + offset, 0, values.length - 1);
        this.setState({
            selectedValue: values[nextIndex]
        });
        return values[nextIndex];
    }

    select() {
        const { selectedValue } = this.state;
        if (selectedValue) {
            this.chooseValue(selectedValue);
        }
    }

    chooseValue(valueItem) {
        this.handleChange(valueItem.value);
        this.setState({
            showList: false,
            editorValue: valueItem.value
        });
    }

    validate(value) {
        if (!this.props.validate) {
            return;
        }
        if (lodash.isNil(value) || (lodash.isString(value) && lodash.isEmpty(value))) {
            this.setState({
                invalid: false
            });
            return;
        }
        const dsItem = this.findDataSourceItem(value);
        this.setState({
            invalid: lodash.isNil(dsItem)
        });
    }

    handleFocus(e) {
        const { onFocus } = this.props;
        if (lodash.isFunction(onFocus)) {
            onFocus(e);
        }
    }

    handleBlur(e) {
        e.stopPropagation();
        const { editorValue } = this.state;
        this.validate(editorValue);
        const { onBlur } = this.props;
        if (lodash.isFunction(onBlur)) {
            onBlur(e);
        }
    }

    handleAutocompleteKeyPress(e) {
        const { selectedValue, showList } = this.state;
        switch (e.key) {
            case 'Enter':
                if (e.shiftKey) {
                    e.stopPropagation();
                    this.focusElement(this.editor);
                } else {
                    if (selectedValue && showList) {
                        e.stopPropagation();
                        this.select();
                    }
                }
                break;
        }
    }

    handleAutocompleteKeyDown(e) {
        const { showList, selectedValue } = this.state;
        switch (e.key) {
            case 'ArrowUp':
                if (e.altKey) {
                    this.setState({ showList: !showList });
                }
                if (showList) {
                    e.stopPropagation();
                    this.selectItemByOffset(-1);
                }
                break;
            case 'ArrowDown':
                if (e.altKey) {
                    this.setState({ showList: !showList });
                }
                if (showList) {
                    e.stopPropagation();
                    this.selectItemByOffset(1);
                }
                break;
            case 'Escape':
                e.stopPropagation();
                if (showList) {
                    this.hideList();
                }
                e.preventDefault();
                break;
            case 'Tab':
                if (e.shiftKey) {
                    e.stopPropagation();
                    this.focusElement(this.editor);
                } else {
                    if (selectedValue && showList) {
                        e.stopPropagation();
                        this.select();
                    }
                }
                break;
        }
    }

    hideList() {
        this.setState({ showList: false });
    }

    handleHintClick(e) {
        const LEFT_MOUSE = 0;
        if (e.button !== LEFT_MOUSE) {
            return;
        }
        this.showList(e);
        e.stopPropagation();
        e.preventDefault();
    }

    searchChangeHandler(newValue) {
        this.setState({ searchValue: newValue || '', editorValue: newValue || '' });
    }

    handleSelectValue(index) {
        const value = this.selectItemByIndex(index);
        this.chooseValue(value);
    }

    // Expected Behavior
    // When enter is pressed on the editor element and the list is not shown,
    // it will show the list and focus on the search box.
    handleEditorEnterPress(e) {
        if (this.isAutocompleteToggleKey(e.key) && !this.state.showList) {
            e.stopPropagation();
            this.showList(e);
        }
    }

    isAutocompleteToggleKey(key) {
        return AutocompleteToggleKeyCheck.test(key);
    }
}

AutocompleteDataSource.propTypes = {
    values: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.any,
        text: PropTypes.string,
        dataType: PropTypes.string,
        order: PropTypes.number
    })),
    validate: PropTypes.bool,
    onValidStateChange: PropTypes.func, // (autocompleteDataSource, { hasError, value }) => void
    dataType: PropTypes.string,
    children: PropTypes.element // Element expected to be DataTypeEditor
};

class AutocompleteSearch extends Component {

    focus() {
        this.searchInput.focus();
    }

    componentDidUpdate({ selectedValue: previousSelectedValue }) {
        if (this.props.selectedValue !== previousSelectedValue) {
            if (!this.valuesArea) {
                return;
            }
            const node = this.valuesArea.querySelector(`.${styles.autocompleteValueSelected}`);
            if (node) {
                this.valuesArea.scrollTop = node.offsetTop - 0.5 * this.valuesArea.clientHeight;
            }

        }
    }

    render() {
        const {
            showList,
            values,
            selectedValue,
            handleSelectValue,
            showDisplayText
        } = this.props;
        if (!showList) {
            return null;
        }

        const renderedValues = lodash.chain(values)
            .map(({ value, text, dataType, order }, index) => {
                const key = `${value}-${order}`;
                const isSelected = selectedValue ? lodash.eq(`${selectedValue.value}-${selectedValue.order}`, key) : false;
                const className = isSelected ? styles.autocompleteValueSelected : styles.autocompleteValue;
                const textNode = showDisplayText ? <span className={styles.autocompleteValueSection}> ({text}) </span> : null;
                return (
                    <div data-test-id="autocomplete-value" className={className} key={key}
                        onMouseDown={e => handleSelectValue(index)} data-cy="AutocompleteDataSource-renderedValues">
                        <DataType className={styles.autocompleteValueSection} value={value} dataType={dataType} />
                        {textNode}
                    </div>
                );
            })
            .value();

        const renderedValuesArea = renderedValues.length > 0
            ? renderedValues
            : <div className={styles.autocompleteNoValues}>No values could be found matching the criteria.</div>;

        return (
            <div className={styles.autocompleteSearchContainer}>
                <div className={styles.autocompleteSearchValuesArea}
                    ref={va => this.valuesArea = va}>
                    {renderedValuesArea}
                </div>
            </div>
        );
    }
}

export default AutocompleteDataSource;
